import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { DriverDocumentType, DriverView, VehicleType } from '../api/types';
import { DOCUMENT_LABELS, EXPIRING_DOCUMENTS, VEHICLE_LABELS } from '../api/types';
import { Alert, Spinner } from '../components/common';

const VEHICLES: VehicleType[] = ['Motorcycle', 'Scooter', 'Car', 'Bicycle', 'OnFoot'];

/**
 * Driver sign-up and document upload.
 *
 * Written for a phone held in one hand at the counter: one document per row,
 * each row saying what it wants and whether it has it. The checklist is
 * generated from what the server says is required for the chosen vehicle, so a
 * cyclist is never asked for a licence and the two cannot drift apart.
 */
export function DriverSignupPage() {
  const [driver, setDriver] = useState<DriverView | null | undefined>(undefined);
  const [vehicle, setVehicle] = useState<VehicleType>('Motorcycle');
  const [registration, setRegistration] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .driverMe()
      .then((d) => {
        setDriver(d ?? null);
        if (d) {
          setVehicle(d.vehicle);
          setRegistration(d.vehicleRegistration ?? '');
        }
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const needsPlate = vehicle !== 'Bicycle' && vehicle !== 'OnFoot';

  async function saveVehicle() {
    setBusy(true);
    setError(null);

    try {
      setDriver(await api.driverRegister(vehicle, needsPlate ? registration.trim() : null));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError(null);

    try {
      setDriver(await api.driverSubmit());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (driver === undefined) return <Spinner label="Loading…" />;

  return (
    <section className="page page--narrow">
      <h1>Deliver for E-Smoothies</h1>

      {error && <Alert>{error}</Alert>}

      {driver && <StatusBanner driver={driver} />}

      <div className="panel">
        <h2 className="panel__title">What do you deliver on?</h2>
        <p className="muted small">
          This decides which documents we need. A bicycle needs no licence or vehicle papers.
        </p>

        <label className="field">
          <span>Vehicle</span>
          <select
            value={vehicle}
            disabled={driver?.status === 'Approved'}
            onChange={(e) => setVehicle(e.target.value as VehicleType)}
          >
            {VEHICLES.map((v) => (
              <option key={v} value={v}>
                {VEHICLE_LABELS[v]}
              </option>
            ))}
          </select>
        </label>

        {needsPlate && (
          <label className="field">
            <span>Registration number</span>
            <input
              type="text"
              value={registration}
              disabled={driver?.status === 'Approved'}
              placeholder="CA 123-456"
              onChange={(e) => setRegistration(e.target.value)}
            />
          </label>
        )}

        {driver?.status !== 'Approved' && (
          <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={saveVehicle}>
            {driver ? 'Update vehicle' : 'Start my application'}
          </button>
        )}
      </div>

      {driver && (
        <>
          <div className="panel">
            <h2 className="panel__title">Your documents</h2>
            <p className="muted small">
              Photos from your phone are fine, as long as everything on the document is readable.
              Max 10&nbsp;MB each.
            </p>

            {driver.required.map((type) => (
              <DocumentRow key={type} type={type} driver={driver} onChange={setDriver} onError={setError} />
            ))}
          </div>

          {driver.canSubmit && (
            <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={submit}>
              Send my application for review
            </button>
          )}

          {driver.status === 'Draft' && !driver.canSubmit && (
            <Alert tone="info">
              Still to upload: {driver.missing.map((m) => DOCUMENT_LABELS[m]).join(', ')}.
            </Alert>
          )}

          {driver.status === 'Approved' && (
            <Link to="/driver" className="btn btn--primary btn--block">
              Go to my deliveries
            </Link>
          )}
        </>
      )}
    </section>
  );
}

function StatusBanner({ driver }: { driver: DriverView }) {
  switch (driver.status) {
    case 'PendingReview':
      return (
        <Alert tone="info">
          <strong>Your application is with the shop.</strong> You&apos;ll get an email when it has been
          looked at. Nothing more to do for now.
        </Alert>
      );
    case 'Approved':
      return (
        <Alert tone="success">
          <strong>You&apos;re approved to deliver.</strong> {driver.completedDeliveries} deliveries
          completed.
        </Alert>
      );
    case 'Rejected':
      return (
        <Alert>
          <strong>Your application wasn&apos;t approved.</strong> {driver.statusReason} Fix what&apos;s
          above and send it again.
        </Alert>
      );
    case 'Suspended':
      return (
        <Alert>
          <strong>Your driver account is paused.</strong> {driver.statusReason}
        </Alert>
      );
    default:
      return null;
  }
}

function DocumentRow({
  type,
  driver,
  onChange,
  onError,
}: {
  type: DriverDocumentType;
  driver: DriverView;
  onChange: (d: DriverView) => void;
  onError: (message: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [expiresOn, setExpiresOn] = useState('');
  const [busy, setBusy] = useState(false);

  const existing = driver.documents.find((d) => d.type === type);
  const expires = EXPIRING_DOCUMENTS.includes(type);

  async function pick(file: File | undefined) {
    if (!file) return;

    if (expires && !expiresOn) {
      onError(`Enter the expiry date on your ${DOCUMENT_LABELS[type].toLowerCase()} first.`);
      if (input.current) input.current.value = '';
      return;
    }

    setBusy(true);
    onError(null);

    try {
      onChange(await api.driverUploadDocument(type, file, expires ? expiresOn : null));
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="doc-row">
      <div className="doc-row__head">
        <strong>{DOCUMENT_LABELS[type]}</strong>
        <DocumentBadge document={existing} />
      </div>

      {existing?.status === 'Rejected' && existing.rejectionReason && (
        <p className="field__error">{existing.rejectionReason} — please upload it again.</p>
      )}

      {existing?.isExpired && <p className="field__error">This has expired. Upload a current one.</p>}

      {expires && (
        <label className="field">
          <span>Expiry date</span>
          <input type="date" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} />
        </label>
      )}

      <input
        ref={input}
        type="file"
        // capture lets a phone open the camera directly, which is how most of
        // these will actually be taken.
        accept="image/*,application/pdf"
        disabled={busy}
        onChange={(e) => pick(e.target.files?.[0])}
      />

      {busy && <span className="muted small">Uploading…</span>}
    </div>
  );
}

function DocumentBadge({ document }: { document: { status: string } | undefined }) {
  if (!document) return <span className="pill pill--muted">Not uploaded</span>;

  switch (document.status) {
    case 'Accepted':
      return <span className="pill pill--good">Accepted</span>;
    case 'Rejected':
      return <span className="pill pill--bad">Rejected</span>;
    default:
      return <span className="pill">Uploaded</span>;
  }
}
